/**
 * Intake Arc
 *
 * Question: "What do we need to know about this patient?"
 *
 * Entry: route → intakePharmacy (or skip based on returning patient data)
 * Exit: done (final) — produces IntakeOutput
 *
 * Actors: none (all intake data already loaded by setupIntake in parent)
 * Signals: GO_BACK (from first interactive state)
 *
 * Navigation map: see README.md
 */

import { and, sendParent, setup } from "xstate";
import type { IntakeInput, IntakeOutput } from "../contracts/arc-contracts";
import type { KioskMachineContext, KioskMachineEvent } from "../types";

// Guards
import { hasCompletedBefore } from "../helper/arc-helpers";

// Actions
import { saveFormData } from "../actions/save-form-data.action";
import { trackSubmittedForm } from "../actions/track-submitted-form.action";

// Screen history helpers (shared with identity arc)
import { peekScreen, popScreen, pushScreen } from "../helper/screen-history";

// ─── Intake Screen Names ──────────────────────────────────
//
// Single source of truth for intake screen names. Used by both the
// forward SUBMIT pushScreen calls and the backRoute transitions, so
// renames and additions are caught in one place.
const INTAKE_SCREEN_NAMES = [
  "intakePharmacy",
  "intakePCP",
  "intakeEmergencyContact",
  "intakeHipaaAgreement",
  "intakeReleaseOfBenefits",
  "intakeConsentToTreatment",
  "intakeCreditCardAuth",
  "intakeReferralSource",
] as const;

type IntakeScreenName = (typeof INTAKE_SCREEN_NAMES)[number];

// Shared submit-action list used by every SUBMIT branch.
// biome-ignore lint/suspicious/noExplicitAny: XState action array typing varies per transition
const submitActionsFor = (screen: IntakeScreenName): any[] => [
  "saveFormData",
  "trackSubmittedForm",
  pushScreen(screen),
];

// ─── Context Helpers ──────────────────────────────────────

/**
 * Seed arc context from input.
 * Uses KioskMachineContext (wide type) initially — see AGENTS.md Typing Strategy.
 */
function createIntakeContext(input: IntakeInput): KioskMachineContext {
  const base: KioskMachineContext = {
    orgId: input.orgId,
    intakeFormSections: input.intakeFormSections,
    responses: input.responses,
    formIds: input.formIds,
    firstName: input.firstName,
    lastName: input.lastName,
    _isReturningPatient: input.isReturningPatient,
    _hipaaExpired: input.hipaaExpired,
    _hasPharmacy: input.hasPharmacy,
    _hasPCP: input.hasPCP,
    _hasEmergencyContact: input.hasEmergencyContact,
    _screenHistory: [],
    submittedFormIds: [],
  };

  // If re-invoked, restore from previous output and flag for routing
  if (input.previousOutput) {
    return {
      ...base,
      ...input.previousOutput,
      // Restore screen history so BACK navigation can walk the
      // previously-pushed screens instead of hitting empty-history
      // and exiting the arc.
      _screenHistory: input.previousOutput._screenHistory ?? [],
      _reInvoked: true,
    };
  }

  return base;
}

/**
 * Extract typed output from arc context at final state.
 */
function extractIntakeOutput(context: KioskMachineContext): IntakeOutput {
  return {
    responses: (context.responses as Record<string, unknown>) || {},
    submittedFormIds: (context.submittedFormIds as string[]) || [],
    _screenHistory: context._screenHistory as string[] | undefined,
  };
}

// ─── Machine Definition ──────────────────────────────────────

export const intakeArc = setup({
  types: {
    context: {} as KioskMachineContext,
    events: {} as KioskMachineEvent,
    input: {} as IntakeInput,
    output: {} as IntakeOutput,
  },
  guards: {
    hasCompletedBefore,
    isReturningWithExpiredHipaa: ({ context }) =>
      !!context._isReturningPatient && !!context._hipaaExpired,
    isReturningWithValidHipaa: ({ context }) =>
      !!context._isReturningPatient && !context._hipaaExpired,
    isReturningPatient: ({ context }) => !!context._isReturningPatient,
    hasValidHipaa: ({ context }) => context._hipaaExpired === false,
    // Skip flags are only emitted by `deriveIntakeSkipFlags` when the
    // returning-patient output carries `infoConfirmed: true`, so a truthy
    // flag here already means "patient explicitly confirmed this data."
    canSkipPharmacy: ({ context }) => !!context._hasPharmacy,
    canSkipPCP: ({ context }) => !!context._hasPCP,
    canSkipEmergencyContact: ({ context }) => !!context._hasEmergencyContact,
  },
  actions: {
    // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
    saveFormData: saveFormData.impl as any,
    // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
    trackSubmittedForm: trackSubmittedForm.impl as any,
    // biome-ignore lint/suspicious/noExplicitAny: XState cross-arc sendParent action typing
    signalGoBackWithResponses: sendParent(({ context }: any) => ({
      type: "GO_BACK" as const,
      responses: context.responses,
    })),
  },
}).createMachine({
  id: "intake",
  context: ({ input }) => createIntakeContext(input),
  output: ({ context }) => extractIntakeOutput(context),
  initial: "route",

  states: {
    // ===== ENTRY ROUTING =====
    route: {
      always: [
        // Re-invocation: jump to exit-adjacent state with data pre-filled
        { guard: "hasCompletedBefore", target: "intakeReferralSource" },
        // Returning patient who confirmed info — all data present, skip to HIPAA or CC auth
        {
          guard: and([
            "isReturningWithExpiredHipaa",
            "canSkipPharmacy",
            "canSkipPCP",
            "canSkipEmergencyContact",
          ]),
          target: "intakeHipaaAgreement",
        },
        {
          guard: and([
            "isReturningWithValidHipaa",
            "canSkipPharmacy",
            "canSkipPCP",
            "canSkipEmergencyContact",
          ]),
          target: "intakeCreditCardAuth",
        },
        // Returning patient with partial data — skip what we can
        {
          guard: and(["isReturningPatient", "canSkipPharmacy", "canSkipPCP"]),
          target: "intakeEmergencyContact",
        },
        {
          guard: and(["isReturningPatient", "canSkipPharmacy"]),
          target: "intakePCP",
        },
        // New patient or no data to skip — full flow
        { target: "intakePharmacy" },
      ],
    },

    // ===== BACK ROUTING (history-based) =====
    // Transitions are derived from INTAKE_SCREEN_NAMES so adding or
    // renaming a screen only requires touching the constant above.
    backRoute: {
      always: [
        ...INTAKE_SCREEN_NAMES.map((name) => ({
          guard: ({ context }: { context: KioskMachineContext }) =>
            peekScreen(context) === name,
          target: name,
          actions: [popScreen()],
        })),
        // Empty history — exit arc
        { actions: "signalGoBackWithResponses" },
      ],
    },

    // ===== PHARMACY =====
    intakePharmacy: {
      meta: {
        componentName: "IntakeQuestions",
        contentKey: "medrite.checkin.intake-pharmacy",
        metadata: {
          sectionName: "Pharmacy",
        },
      },
      on: {
        SUBMIT: [
          {
            guard: and([
              "canSkipPCP",
              "canSkipEmergencyContact",
              "hasValidHipaa",
            ]),
            target: "intakeCreditCardAuth",
            actions: submitActionsFor("intakePharmacy"),
          },
          {
            guard: and(["canSkipPCP", "canSkipEmergencyContact"]),
            target: "intakeHipaaAgreement",
            actions: submitActionsFor("intakePharmacy"),
          },
          {
            guard: "canSkipPCP",
            target: "intakeEmergencyContact",
            actions: submitActionsFor("intakePharmacy"),
          },
          {
            target: "intakePCP",
            actions: submitActionsFor("intakePharmacy"),
          },
        ],
        BACK: { target: "backRoute" },
      },
    },

    // ===== PRIMARY CARE PROVIDER =====
    intakePCP: {
      meta: {
        componentName: "IntakeQuestions",
        contentKey: "medrite.checkin.intake-pcp",
        metadata: {
          sectionName: "Primary Care Provider",
        },
      },
      on: {
        SUBMIT: [
          {
            guard: and(["canSkipEmergencyContact", "hasValidHipaa"]),
            target: "intakeCreditCardAuth",
            actions: submitActionsFor("intakePCP"),
          },
          {
            guard: "canSkipEmergencyContact",
            target: "intakeHipaaAgreement",
            actions: submitActionsFor("intakePCP"),
          },
          {
            target: "intakeEmergencyContact",
            actions: submitActionsFor("intakePCP"),
          },
        ],
        BACK: { target: "backRoute" },
      },
    },

    // ===== EMERGENCY CONTACT =====
    intakeEmergencyContact: {
      meta: {
        componentName: "IntakeQuestions",
        contentKey: "medrite.checkin.intake-emergency-contact",
        metadata: {
          sectionName: "Emergency Contact",
        },
      },
      on: {
        SUBMIT: [
          {
            guard: "hasValidHipaa",
            target: "intakeCreditCardAuth",
            actions: submitActionsFor("intakeEmergencyContact"),
          },
          {
            target: "intakeHipaaAgreement",
            actions: submitActionsFor("intakeEmergencyContact"),
          },
        ],
        BACK: { target: "backRoute" },
      },
    },

    // ===== HIPAA AGREEMENT =====
    intakeHipaaAgreement: {
      meta: {
        componentName: "IntakeQuestions",
        contentKey: "medrite.checkin.intake-hipaa-agreement",
        metadata: {
          sectionName: "HIPAA Agreement",
        },
      },
      on: {
        SUBMIT: {
          target: "intakeReleaseOfBenefits",
          actions: submitActionsFor("intakeHipaaAgreement"),
        },
        BACK: { target: "backRoute" },
      },
    },

    // ===== RELEASE OF BENEFITS =====
    intakeReleaseOfBenefits: {
      meta: {
        componentName: "IntakeQuestions",
        contentKey: "medrite.checkin.intake-billing-release",
        metadata: {
          sectionName: "Release of Benefits",
        },
      },
      on: {
        SUBMIT: {
          target: "intakeConsentToTreatment",
          actions: submitActionsFor("intakeReleaseOfBenefits"),
        },
        BACK: { target: "backRoute" },
      },
    },

    // ===== CONSENT TO TREATMENT =====
    intakeConsentToTreatment: {
      meta: {
        componentName: "IntakeQuestions",
        contentKey: "medrite.checkin.intake-consent-to-treat",
        metadata: {
          sectionName: "Consent to Treatment",
        },
      },
      on: {
        SUBMIT: {
          target: "intakeCreditCardAuth",
          actions: submitActionsFor("intakeConsentToTreatment"),
        },
        BACK: { target: "backRoute" },
      },
    },

    // ===== CREDIT CARD AUTHORIZATION =====
    intakeCreditCardAuth: {
      meta: {
        componentName: "IntakeQuestions",
        contentKey: "medrite.checkin.intake-credit-card-auth",
        metadata: {
          sectionName: "Credit Card Authorization",
        },
      },
      on: {
        SUBMIT: {
          target: "intakeReferralSource",
          actions: submitActionsFor("intakeCreditCardAuth"),
        },
        BACK: { target: "backRoute" },
      },
    },

    // ===== REFERRAL SOURCE =====
    intakeReferralSource: {
      meta: {
        componentName: "IntakeQuestions",
        contentKey: "medrite.checkin.intake-referral-source",
        metadata: {
          sectionName: "Referral Source",
        },
      },
      on: {
        // NOTE: SUBMIT intentionally does NOT push "intakeReferralSource".
        // The BACK-chain invariant is "history top = the previous screen,
        // not the current one". intakeReferralSource is the terminal
        // interactive screen (SUBMIT → done), so pushing it would leave
        // the current screen on top of history on re-invocation. BACK
        // from the re-entered intakeReferralSource would then target
        // intakeReferralSource itself (peek matches current) instead of
        // walking back to intakeCreditCardAuth. Mirrors the identity
        // arc's sexInput convention.
        SUBMIT: {
          target: "done",
          actions: ["saveFormData", "trackSubmittedForm"],
        },
        BACK: { target: "backRoute" },
      },
    },

    // ===== EXIT =====
    done: {
      type: "final" as const,
    },
  },
});
